import db from "./db";
import { api } from "./api";
import { clientOfflineAllowed, getPrivacySession, privacySignature } from "./privacy-session";

let _pendingCountListeners = [];
let _syncStatusListeners = [];
let _lastSyncedListeners = [];
let _cacheWarmingListeners = [];

export function subscribePendingCount(fn) {
  _pendingCountListeners.push(fn);
  return () => { _pendingCountListeners = _pendingCountListeners.filter((f) => f !== fn); };
}

export function subscribeSyncStatus(fn) {
  _syncStatusListeners.push(fn);
  return () => { _syncStatusListeners = _syncStatusListeners.filter((f) => f !== fn); };
}

export function subscribeLastSynced(fn) {
  _lastSyncedListeners.push(fn);
  return () => { _lastSyncedListeners = _lastSyncedListeners.filter((f) => f !== fn); };
}

export function subscribeCacheWarming(fn) {
  _cacheWarmingListeners.push(fn);
  return () => { _cacheWarmingListeners = _cacheWarmingListeners.filter((f) => f !== fn); };
}

async function notifyPendingCount() {
  const count = await db.pendingOps.count();
  _pendingCountListeners.forEach((fn) => fn(count));
}

function notifySyncStatus(status) {
  _syncStatusListeners.forEach((fn) => fn(status));
}

function notifyLastSynced(ts) {
  _lastSyncedListeners.forEach((fn) => fn(ts));
}

function notifyCacheWarming(isWarming) {
  _cacheWarmingListeners.forEach((fn) => fn(isWarming));
}

export async function getPendingCount() {
  return db.pendingOps.count();
}

export async function getLastSynced() {
  const meta = await db.syncMeta.get("clients_last_synced");
  return meta?.value || null;
}

// ---------------------------------------------------------------------------
// Cache warming — fetches ALL clients in pages and upserts into Dexie
// ---------------------------------------------------------------------------
const WARM_PAGE_SIZE = 200;
let _isWarming = false;

export async function warmCache() {
  if (!clientOfflineAllowed()) return;
  const session = privacySignature(getPrivacySession());
  if (_isWarming) return;
  _isWarming = true;
  notifyCacheWarming(true);
  try {
    let skip = 0;
    let fetched = 0;
    let total = null;

    while (true) {
      const { data } = await api.get(
        `/clients?skip=${skip}&limit=${WARM_PAGE_SIZE}&search=&filter=&sort_by=last_name&sort_dir=asc`
      );

      const page = data.clients || [];
      if (!clientOfflineAllowed() || session !== privacySignature(getPrivacySession())) return;
      if (total === null) total = data.total ?? 0;

      if (page.length > 0) {
        const records = page.map((c) => ({ ...c, _id: c.id }));
        await db.clients.bulkPut(records);
      }

      fetched += page.length;

      if (page.length < WARM_PAGE_SIZE || fetched >= total) break;
      skip += page.length;
    }

    const ts = new Date().toISOString();
    await db.syncMeta.put({ key: "clients_last_synced", value: ts });
    notifyLastSynced(ts);
  } catch {
    // Warming failed silently — staff won't lose any pending ops
  } finally {
    _isWarming = false;
    notifyCacheWarming(false);
  }
}

// ---------------------------------------------------------------------------
// Pending-ops sync — uploads local mutations to the server
// ---------------------------------------------------------------------------
export async function syncPendingOps() {
  if (!clientOfflineAllowed()) return;
  const session = privacySignature(getPrivacySession());
  const ops = await db.pendingOps.orderBy("id").toArray();
  if (ops.length === 0) return;

  notifySyncStatus("syncing");

  const idRemapping = {};

  for (const op of ops) {
    if (!clientOfflineAllowed() || session !== privacySignature(getPrivacySession())) return;
    try {
      if (op.type === "create") {
        const tempId = op.payload._id;
        const { _id, ...payloadWithoutId } = op.payload;
        const { data } = await api.post("/clients", payloadWithoutId);
        const serverId = data.id || data._id;

        if (serverId && tempId) {
          idRemapping[tempId] = serverId;
          const localRecord = await db.clients.get(tempId);
          if (localRecord) {
            await db.clients.delete(tempId);
            await db.clients.put({ ...localRecord, _id: serverId, _pendingSync: false });
          }
        }
        await db.pendingOps.delete(op.id);

      } else if (op.type === "update") {
        let clientId = op.payload.id;
        if (idRemapping[clientId]) clientId = idRemapping[clientId];
        if (clientId && !clientId.startsWith("pending_")) {
          await api.put(`/clients/${clientId}`, op.payload.data);
          const localRecord = await db.clients.get(clientId);
          if (localRecord) {
            await db.clients.put({ ...localRecord, _pendingSync: false });
          }
        }
        await db.pendingOps.delete(op.id);

      } else if (op.type === "checkin") {
        let clientId = op.payload.clientId;
        if (idRemapping[clientId]) clientId = idRemapping[clientId];
        if (clientId && !clientId.startsWith("pending_")) {
          await api.post(`/clients/${clientId}/visits`, {
            override: op.payload.override || false,
            bag_ids: op.payload.bag_ids || [],
            visit_date: op.payload.visit_date || null,
          });
          const localRecord = await db.clients.get(clientId);
          if (localRecord) {
            await db.clients.put({ ...localRecord, _pendingSync: false });
          }
        }
        await db.pendingOps.delete(op.id);

      } else if (op.type === "update_member") {
        await api.put(`/members/${op.payload.memberId}`, op.payload.data);
        await db.pendingOps.delete(op.id);

      } else if (op.type === "add_member") {
        let clientId = op.payload.clientId;
        if (idRemapping[clientId]) clientId = idRemapping[clientId];
        if (clientId && !clientId.startsWith("pending_")) {
          await api.post(`/clients/${clientId}/members`, op.payload.data);
        }
        await db.pendingOps.delete(op.id);

      } else if (op.type === "delete_member") {
        await api.delete(`/members/${op.payload.memberId}`);
        await db.pendingOps.delete(op.id);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Sync failed";
      await db.pendingOps.update(op.id, { syncError: msg });
    }
  }

  await notifyPendingCount();
  const remaining = await db.pendingOps.count();
  notifySyncStatus(remaining === 0 ? "synced" : "error");
}

export async function queueOp(type, payload) {
  await db.pendingOps.add({ type, payload, createdAt: new Date().toISOString(), syncError: null });
  await notifyPendingCount();
}
